import { Box } from "@chakra-ui/react";
import { flexRender, getCoreRowModel, getPaginationRowModel, getSortedRowModel, useReactTable } from "@tanstack/react-table";
import { useState, useEffect } from "react";

import getCols from "../../utils/business/columns";
import ReactiveHeader from "./reactiveHeader";

/**
 * MainHeader component renders a table header and optionally its rows based on the provided data and columns.
 *
 * @component
 * @param {Object} props - The properties object.
 * @param {boolean} props.show - Flag to determine whether to show the table rows (use for hide rows toggle).
 * @param {Array} props.data - The data to be displayed in the table.
 * @param {string} props.type - The type of columns to be generated.
 *
 * @returns {JSX.Element} The rendered MainHeader component.
 *
 * @example
 * <MainHeader show={true} data={data} type="exampleType" />
 */
const MainHeader = (props) => {

  const show = props.show;

  const [data, setData] = useState([]);

  useEffect(() => {
    setData(props.data);
  }, [props]);

  const columns = getCols(data, props.type);

  const table = useReactTable({
    data,
    columns,
    manualPagination: true,
    getSortedRowModel: getSortedRowModel(),
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    columnResizeMode: "onChange"
  });

  return (
    <Box>
      <Box className="table" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }} w={table.getTotalSize()}>
        {table.getHeaderGroups().map((headerGroup) => (
          <Box className="tr" key={headerGroup.id}>
            {headerGroup.headers.map((header, index) => (
              <ReactiveHeader key={header.id} header={header} index={index} type={"main"} />
            ))}
          </Box>
        ))}
        {show ?
          <Box>
            {table.getRowModel().rows.map((row) => (
              <Box className="tr" key={row.id}>
                {row.getVisibleCells().map((cell, index) => (
                  <Box className={`td ${index === 0 ? "text-left m-0 p-0 pl-4" :
                    index !== 0 && index % 2 !== 0 ? "text-right tr-mid m-0 p-0 pr-6" : "text-right m-0 p-0 pr-6"}`} w={cell.column.getSize() - 3} key={cell.id} fontSize='xs' py="0.5">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Box>
                ))}
              </Box>
            ))}
          </Box> : null}
      </Box>
    </Box>
  );
};
export default MainHeader;
